import type { ProductModule, ResearchNote, Task, WorkflowStageKind } from "../api";

export const statusText: Record<Task["status"], string> = {
  DRAFT: "초안", APPROVAL_PENDING: "발신 승인 대기", REQUESTED: "응답 대기", REJECTED: "반려", IN_REVIEW: "판정 대기", CONFIRMED: "착수 대기", IN_PROGRESS: "진행 중", VERIFYING: "검증 중", COMPLETION_REVIEW: "완료 대기", COMPLETED: "완료", CANCELLED: "취소",
};
export const priorityText = { LOW: "낮음", NORMAL: "보통", HIGH: "높음", URGENT: "긴급" } as const;
export const difficultyText = { LOW: "하", MEDIUM: "중", HIGH: "상" } as const;
export const difficultyRank = { LOW: 1, MEDIUM: 2, HIGH: 3 } as const;
export const workflowKindText: Record<WorkflowStageKind, string> = { WORK: "작업", REVIEW: "검토", DECISION: "의사결정", TEAM: "팀 실행", COMPLETE: "완료" };
export const noteStatusText: Record<ResearchNote["status"], string> = { DRAFT: "작성 중", AWAITING_REVIEW: "점검 대기", REJECTED: "보완 필요", FINALIZED: "기록자 확정", REVIEWED: "점검 승인" };
export const moduleLabels: Record<ProductModule, string> = { TASKS: "업무", RESEARCH_NOTES: "연구노트", PERFORMANCE: "성과", COLLABORATION: "협업", PROJECTS: "프로젝트", ATTENDANCE: "출퇴근 관리", BOARD: "게시판", STATUS: "현황" };
export const roundStatusText: Record<Task["rounds"][number]["status"], string> = { OPEN: "작성 중", SUBMITTED: "판정 대기", ACCEPTED: "승인", REVISION_REQUIRED: "보완 요청" };

export const isoDate = (value?: string | null) => value ? value.slice(0, 10) : "";
export const koDate = (value?: string | null) => value ? new Intl.DateTimeFormat("ko-KR", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value)) : "미정";
export const koTime = (value?: string | null) => value ? new Intl.DateTimeFormat("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value)) : "";
export const koStamp = (value?: string | null) => value ? `${value.slice(0, 10)} ${value.slice(11, 19)}` : "";
export const initials = (name: string) => name.trim().slice(0, 2).toUpperCase();

const DAY_MS = 86400000;

function calendarDay(value?: string | null): number | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

function localCalendarDay(value: Date): number {
  return Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / DAY_MS;
}

export function dayCount(dueOn?: string | null, from = new Date()): number | null {
  const due = calendarDay(dueOn);
  return due === null ? null : due - localCalendarDay(from);
}

export type TaskDeadlineTone = "normal" | "warning" | "near" | "critical" | "overdue";

export function taskDeadlineProgress(
  task: Pick<Task, "startsOn" | "dueOn" | "status">,
  from = new Date(),
): { percent: number; tone: TaskDeadlineTone } | null {
  if (task.status === "COMPLETED" || task.status === "CANCELLED") return null;
  const start = calendarDay(task.startsOn);
  const due = calendarDay(task.dueOn);
  if (start === null || due === null || due < start) return null;

  const today = localCalendarDay(from);
  const percent = due === start
    ? (today >= due ? 100 : 0)
    : Math.max(0, Math.min(100, Math.round(((today - start) / (due - start)) * 100)));
  const tone: TaskDeadlineTone = today >= due ? "overdue"
    : percent >= 90 ? "critical"
      : percent >= 75 ? "near"
        : percent > 50 ? "warning"
          : "normal";
  return { percent, tone };
}

export function ddayText(task: Pick<Task, "dueOn" | "status">): string {
  if (task.status === "COMPLETED") return "완료";
  if (task.status === "CANCELLED") return "취소";
  const days = dayCount(task.dueOn);
  if (days === null) return "미정";
  if (days === 0) return "D-day";
  return days > 0 ? `D-${days}` : `D+${Math.abs(days)}`;
}

export function ddayTone(task: Pick<Task, "dueOn" | "status">): "" | "soon" | "late" | "done" {
  if (task.status === "COMPLETED" || task.status === "CANCELLED") return "done";
  const days = dayCount(task.dueOn);
  if (days === null) return "";
  if (days < 0) return "late";
  return days <= 2 ? "soon" : "";
}

export const currentRound = (task: Task) => task.rounds.at(-1);
export const sealedRounds = (task: Task) => task.rounds.filter((round) => round.status === "ACCEPTED" || round.status === "REVISION_REQUIRED");
export const openRound = (task: Task) => task.rounds.find((round) => round.status === "OPEN" || round.status === "SUBMITTED");
